import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { createAccount, editAccount, resetTempPassword } from "../../api/auth";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";

import JsBarcode from "jsbarcode";
import {
  ROLE_JDTECH_ADMIN,
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
} from "../../utils/constants";
import { getAssignableLocations } from "./locationOptions";
import TimeOffDialog from "../time-off/TimeOffDialog";
import TimeOffList from "../time-off/TimeOffList";
import { deleteTimeOff, listTimeOff } from "../time-off/timeOffApi";
import dayjs from "dayjs";

import {
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const DayOfWeek = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

const DEFAULT_AVAILABILITY = {
  MONDAY: { startTime: "09:00", endTime: "17:00", isAvailable: true },
  TUESDAY: { startTime: "09:00", endTime: "17:00", isAvailable: true },
  WEDNESDAY: { startTime: "09:00", endTime: "17:00", isAvailable: true },
  THURSDAY: { startTime: "09:00", endTime: "17:00", isAvailable: true },
  FRIDAY: { startTime: "09:00", endTime: "17:00", isAvailable: true },
  SATURDAY: { startTime: "09:00", endTime: "17:00", isAvailable: false },
  SUNDAY: { startTime: "09:00", endTime: "17:00", isAvailable: false },
};

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "DELETED", label: "Deleted" },
];

/** Match manage-users table status chips */
const STATUS_CHIP_COLOR = {
  ACTIVE: "success",
  PENDING: "warning",
  INACTIVE: "default",
  DELETED: "error",
};

const BARCODE_RENDER_OPTS = {
  format: "CODE128",
  height: 20,
  width: 1,
  margin: 0,
  displayValue: false,
};

function cloneAvailability(source = DEFAULT_AVAILABILITY) {
  return Object.fromEntries(
    Object.entries(source).map(([k, v]) => [
      k,
      {
        startTime: v.startTime,
        endTime: v.endTime,
        isAvailable: v.isAvailable,
      },
    ])
  );
}

function newBarcode() {
  return Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join(
    ""
  );
}

function isValidEmail(email) {
  const s = String(email ?? "").trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function normalizePhoneDigits(phone) {
  const digits = String(phone ?? "").trim().replace(/[^\d]/g, "");
  if (digits.length === 11) return digits.slice(-10);
  return digits;
}

export default function CreateAccountForm(props = {}) {
  const {
    userId: userIdProp,
    stylistPreset,
    onSaved,
    onUserUpdated,
    onRequestClose,
  } = props;

  const { user: userAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const routeUserId = params?.id;

  const effectiveUserId = useMemo(() => {
    if (userIdProp != null && userIdProp !== "") return String(userIdProp);
    return routeUserId || undefined;
  }, [userIdProp, routeUserId]);

  const searchParams = new URLSearchParams(location.search);
  const stylistRole = stylistPreset ?? searchParams.get("role");

  const [isFetching, setIsFetching] = useState(!!effectiveUserId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generateBarcode, setGenerateBarcode] = useState(false);
  const [barcodePreviewUrl, setBarcodePreviewUrl] = useState(null);
  const [locations, setLocations] = useState([]);
  // Locations the edited user had when the form loaded.
  const [savedLocationIds, setSavedLocationIds] = useState([]);
  const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
    useState(false);
  const [isResetPasswordConfirmOpen, setIsResetPasswordConfirmOpen] =
    useState(false);

  const [form, setForm] = useState(() => ({
    user_first_name: "",
    user_last_name: "",
    user_email: "",
    user_phone: "",
    location_id: [],
    roles: stylistRole ? [1] : [],
    user_barcode: null,
    user_status: "ACTIVE",
    availability: cloneAvailability(),
  }));

  const fieldSize = "small";
  const blockSpacing = 1.75;

  const roleOptions = useMemo(() => {
    if (stylistRole) return [{ id: 1, name: "stylist" }];
    if (userAuth.userRoles.includes(ROLE_SYSTEM_ADMIN)) {
      return [
        { id: 1, name: "stylist" },
        { id: 2, name: "salon admin" },
        { id: 3, name: "system admin" },
        { id: 4, name: "front desk" },
      ];
    }
    return [
      { id: 1, name: "stylist" },
      { id: 2, name: "salon admin" },
      { id: 4, name: "front desk" },
    ];
  }, [stylistRole, userAuth.userRoles]);

  const selectedRoleObjects = useMemo(
    () => roleOptions.filter((r) => form.roles.includes(r.id)),
    [roleOptions, form.roles]
  );

  // JD-427: a salon admin may assign only their own locations.
  const locationOptions = useMemo(
    () => getAssignableLocations(locations, userAuth, savedLocationIds),
    [locations, userAuth, savedLocationIds]
  );

  const busy = isFetching || isSubmitting;

  // Stylist time off (JD-465): saved straight to the API, not with the form.
  const [timeOffBlocks, setTimeOffBlocks] = useState([]);
  const [timeOffDialog, setTimeOffDialog] = useState(null); // { timeOff? }
  const canManageTimeOff = userAuth.userRoles.some((role) =>
    [ROLE_SYSTEM_ADMIN, ROLE_SALON_ADMIN, ROLE_JDTECH_ADMIN].includes(role)
  );
  const showTimeOff = Boolean(effectiveUserId) && form.roles.includes(1);

  const loadTimeOff = useCallback(async () => {
    if (!effectiveUserId) return;
    try {
      // An explicit far `to`: the API default (+90 days) would hide blocks
      // recorded further ahead.
      const blocks = await listTimeOff({
        userId: effectiveUserId,
        from: dayjs().startOf("day"),
        to: dayjs().startOf("day").add(5, "year"),
      });
      setTimeOffBlocks(blocks);
    } catch (error) {
      console.error("Error fetching time off:", error);
    }
  }, [effectiveUserId]);

  useEffect(() => {
    if (showTimeOff) loadTimeOff();
  }, [showTimeOff, loadTimeOff]);

  const handleDeleteTimeOff = async (block) => {
    try {
      await deleteTimeOff(block.id);
      toast.success("Time off deleted");
      await loadTimeOff();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete time off");
    }
  };

  useEffect(() => {
    async function getLocations() {
      try {
        const res = await baseAxios.get("/organization/-/locations");
        setLocations(res.data.locations ?? []);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    }
    getLocations();
  }, []);

  useEffect(() => {
    const fetchUserById = async () => {
      try {
        if (!locations.length) return;

        setIsFetching(true);
        const response = await baseAxios.get(`/users/${effectiveUserId}`);
        const user = response.data.user;

        if (!user) return;

        const hasBarcode = !!user.user_barcode;
        let roleIds = [];
        if (user.roles && Array.isArray(user.roles)) {
          roleIds = user.roles
            .filter((role) => role && role.id != null)
            .map((role) => role.id);
        }

        // location_id[0] is the home (primary) location; the rest are additional.
        const assignedIds = Array.isArray(user.locations)
          ? user.locations
              .map((loc) => loc.location_id)
              .filter((id) => id != null)
          : [];
        const primaryId = user.location_id ?? assignedIds[0] ?? null;
        const locIds =
          primaryId == null
            ? assignedIds
            : [primaryId, ...assignedIds.filter((id) => id !== primaryId)];

        const baseAvail = cloneAvailability();
        const transformedAvailability =
          user.stylistAvailability?.reduce((acc, item) => {
            acc[item.day_of_week] = {
              startTime: item.start_time,
              endTime: item.end_time,
              isAvailable: true,
            };
            return acc;
          }, {}) || {};

        const availability = { ...baseAvail };
        Object.keys(availability).forEach((day) => {
          if (transformedAvailability[day]) {
            availability[day] = {
              ...availability[day],
              ...transformedAvailability[day],
              isAvailable: true,
            };
          } else {
            availability[day] = {
              ...availability[day],
              isAvailable: false,
            };
          }
        });

        setForm({
          user_first_name: user.user_first_name ?? "",
          user_last_name: user.user_last_name ?? "",
          user_email: user.user_email ?? "",
          user_phone: user.user_phone ?? "",
          location_id: locIds,
          roles: roleIds,
          user_barcode: user.user_barcode ?? null,
          user_status: user.user_status ?? "ACTIVE",
          availability,
        });
        setSavedLocationIds(locIds);
        setGenerateBarcode(hasBarcode);
      } catch (error) {
        toast.error("Error loading user data", { duration: 3000 });
      } finally {
        setIsFetching(false);
      }
    };
    if (effectiveUserId && locations.length > 0) {
      fetchUserById();
    }
  }, [effectiveUserId, locations]);

  useEffect(() => {
    if (!generateBarcode) {
      setForm((prev) =>
        prev.user_barcode != null ? { ...prev, user_barcode: null } : prev
      );
      setBarcodePreviewUrl(null);
      return;
    }
    setForm((prev) =>
      prev.user_barcode ? prev : { ...prev, user_barcode: newBarcode() }
    );
  }, [generateBarcode]);

  useEffect(() => {
    if (!generateBarcode || !form.user_barcode) {
      setBarcodePreviewUrl(null);
      return;
    }
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, String(form.user_barcode), BARCODE_RENDER_OPTS);
      setBarcodePreviewUrl(canvas.toDataURL());
    } catch {
      setBarcodePreviewUrl(null);
    }
  }, [generateBarcode, form.user_barcode]);

  const handleRegenerateBarcode = useCallback(() => {
    setForm((prev) => ({ ...prev, user_barcode: newBarcode() }));
  }, []);

  const downloadBarcodePng = useCallback(() => {
    if (!form.user_barcode) return;
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(form.user_barcode), BARCODE_RENDER_OPTS);
    const link = document.createElement("a");
    link.download = "barcode.png";
    link.href = canvas.toDataURL();
    link.click();
  }, [form.user_barcode]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const {
        user_first_name,
        user_last_name,
        user_email,
        user_phone,
        location_id,
        roles,
        user_barcode,
        user_status,
        availability,
      } = form;

      const fn = String(user_first_name ?? "").trim();
      const ln = String(user_last_name ?? "").trim();
      const em = String(user_email ?? "").trim();
      const ph = normalizePhoneDigits(user_phone);

      if (!fn) {
        toast.error("First name is required", { duration: 4000 });
        return;
      }
      if (!ln) {
        toast.error("Last name is required", { duration: 4000 });
        return;
      }
      if (!em || !isValidEmail(em)) {
        toast.error("A valid email is required", { duration: 4000 });
        return;
      }
      if (!roles || !roles.length) {
        toast.error("User must be assigned at least one role", { duration: 5000 });
        return;
      }
      if (!location_id || !location_id.length) {
        toast.error("User must be assigned a location", { duration: 5000 });
        return;
      }

      const stylistAvailability = availability
        ? Object.entries(availability)
            .filter(([, dayData]) => dayData && dayData.isAvailable)
            .map(([day, dayData]) => ({
              day_of_week: day,
              start_time: dayData.startTime || "09:00",
              end_time: dayData.endTime || "17:00",
            }))
        : [];

      setIsSubmitting(true);

      let result;
      if (effectiveUserId) {
        result = await editAccount(
          effectiveUserId,
          fn,
          ln,
          em,
          ph,
          roles,
          `${location_id[0]}`,
          location_id,
          generateBarcode ? user_barcode : null,
          user_status,
          stylistAvailability
        );
      } else {
        result = await createAccount(
          fn,
          ln,
          em,
          ph,
          roles,
          `${location_id[0]}`,
          location_id,
          generateBarcode ? user_barcode : null,
          stylistAvailability
        );
      }
      setIsSubmitting(false);

      if (result.isOk) {
        toast.success(result.message, { duration: 3000 });
        if (typeof onSaved === "function") onSaved();
        else navigate("/manage-users");
      } else {
        toast.error(result.message, { duration: 5000 });
      }
    },
    [form, navigate, effectiveUserId, generateBarcode, onSaved]
  );

  const handleCancel = () => {
    setIsConfirmationPopupVisible(false);
  };

  const handleConfirm = async () => {
    setIsConfirmationPopupVisible(false);

    const {
      user_first_name,
      user_last_name,
      user_email,
      user_phone,
      location_id,
      roles,
      user_barcode,
      availability,
    } = form;

    const ph = normalizePhoneDigits(user_phone);

    const stylistAvailability = availability
      ? Object.entries(availability)
          .filter(([, dayData]) => dayData && dayData.isAvailable)
          .map(([day, dayData]) => ({
            day_of_week: day,
            start_time: dayData.startTime || "09:00",
            end_time: dayData.endTime || "17:00",
          }))
      : [];

    setIsSubmitting(true);
    const result = await editAccount(
      effectiveUserId,
      user_first_name,
      user_last_name,
      user_email,
      ph,
      roles,
      `${location_id[0]}`,
      location_id,
      user_barcode,
      "INACTIVE",
      stylistAvailability
    );
    setIsSubmitting(false);

    if (!result?.isOk) {
      toast.error(result?.message ?? "Could not deactivate account.", {
        duration: 5000,
      });
      return;
    }

    toast.success("Account deactivated.", { duration: 3000 });
    if (typeof onSaved === "function") onSaved();
    else navigate("/manage-users");
  };

  const handleResetPassword = useCallback(async () => {
    const email = String(form.user_email ?? "").trim();
    if (!email) {
      toast.error("User email is missing.", { duration: 3000 });
      return;
    }
    setIsSubmitting(true);
    const result = await resetTempPassword(email);
    setIsSubmitting(false);
    if (result?.isOk) {
      toast.success("Temporary password sent to user's email.", { duration: 3000 });
      setForm((p) => ({ ...p, user_status: "PENDING" }));
      if (typeof onUserUpdated === "function") onUserUpdated();
    } else {
      toast.error(result?.message ?? "Could not send reset email.", { duration: 5000 });
    }
  }, [form.user_email, onUserUpdated]);

  const handleResetPasswordCancel = useCallback(() => {
    setIsResetPasswordConfirmOpen(false);
  }, []);

  const handleResetPasswordConfirm = useCallback(async () => {
    setIsResetPasswordConfirmOpen(false);
    await handleResetPassword();
  }, [handleResetPassword]);

  const selectedLocationIds = form.location_id ?? [];
  const selectedLocationObjects = selectedLocationIds
    .map((id) => locations.find((loc) => loc.location_id === id))
    .filter(Boolean);
  const primaryLocationValue = selectedLocationIds[0] ?? "";

  const handleLocationsChange = (_, value) => {
    setForm((p) => {
      // Keep assignments the editor can't see (not in their location list).
      const hiddenIds = (p.location_id ?? []).filter(
        (id) => !locations.some((loc) => loc.location_id === id)
      );
      const ids = [...value.map((loc) => loc.location_id), ...hiddenIds];
      const currentPrimary = p.location_id?.[0];
      return {
        ...p,
        location_id: ids.includes(currentPrimary)
          ? [currentPrimary, ...ids.filter((id) => id !== currentPrimary)]
          : ids,
      };
    });
  };

  const handlePrimaryLocationChange = (e) => {
    const primary = e.target.value;
    setForm((p) => ({
      ...p,
      location_id: [
        primary,
        ...(p.location_id ?? []).filter((id) => id !== primary),
      ],
    }));
  };

  const sectionLabelSx = {
    fontWeight: 600,
    letterSpacing: 0.06,
    lineHeight: 1,
    color: "text.secondary",
    display: "block",
    mb: 1.25,
    mt: 1,
  };

  const pageShell = (children) => (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </Box>
  );

  const formScrollSx = {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    px: 2.5,
    pt: 1.5,
    pb: 2,
    position: "relative",
  };

  const footerSx = {
    flexShrink: 0,
    borderTop: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
    px: 2.5,
    py: 2,
    mt: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 1,
  };

  const formBody = (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={formScrollSx}>
        {isFetching ? (
          <>
            <LinearProgress
              sx={{
                position: "sticky",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.45)"
                    : "rgba(255,255,255,0.72)",
                backdropFilter: "blur(4px)",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <CircularProgress size={20} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Loading user…
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pulling profile, roles, location, and availability
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        ) : null}
        <Stack spacing={blockSpacing} sx={{ width: "100%" }}>
          <Box>
            <Typography variant="overline" sx={sectionLabelSx}>
              Profile
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First name"
                  name="user_first_name"
                  value={form.user_first_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, user_first_name: e.target.value }))
                  }
                  fullWidth
                  required
                  size={fieldSize}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last name"
                  name="user_last_name"
                  value={form.user_last_name}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, user_last_name: e.target.value }))
                  }
                  fullWidth
                  required
                  size={fieldSize}
                  disabled={busy}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="user_email"
                  type="email"
                  value={form.user_email}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, user_email: e.target.value }))
                  }
                  fullWidth
                  required
                  size={fieldSize}
                  disabled={busy || !!effectiveUserId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  name="user_phone"
                  value={form.user_phone}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, user_phone: e.target.value }))
                  }
                  fullWidth
                  placeholder="+1 (555) 000-0000"
                  size={fieldSize}
                  disabled={busy}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="overline" sx={sectionLabelSx}>
              Role & location
            </Typography>
            <Grid container spacing={1.25} columnSpacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  // JD-427: a salon admin may assign only their own locations.
                  options={locationOptions}
                  getOptionLabel={(o) => o.location_name ?? ""}
                  isOptionEqualToValue={(o, v) => o.location_id === v.location_id}
                  value={selectedLocationObjects}
                  onChange={handleLocationsChange}
                  disabled={busy}
                  size={fieldSize}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Salons / locations"
                      placeholder={
                        selectedLocationObjects.length ? "" : "Select locations"
                      }
                      required={selectedLocationObjects.length === 0}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={roleOptions}
                  getOptionLabel={(o) => o.name}
                  value={selectedRoleObjects}
                  onChange={(_, v) =>
                    setForm((p) => ({ ...p, roles: v.map((x) => x.id) }))
                  }
                  disabled={busy}
                  size={fieldSize}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Roles"
                      placeholder="Select roles"
                    />
                  )}
                />
              </Grid>
              {selectedLocationObjects.length > 1 ? (
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required
                    size={fieldSize}
                    disabled={busy}
                  >
                    <InputLabel id="primary-location-select-label">
                      Home salon
                    </InputLabel>
                    <Select
                      labelId="primary-location-select-label"
                      label="Home salon"
                      value={primaryLocationValue}
                      onChange={handlePrimaryLocationChange}
                    >
                      {selectedLocationObjects.map((loc) => (
                        <MenuItem key={loc.location_id} value={loc.location_id}>
                          {loc.location_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ) : null}
              {effectiveUserId ? (
                <Grid item xs={12}>
                  <Typography variant="overline" sx={sectionLabelSx}>
                    STATUS
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={0.75}
                  >
                    {STATUS_OPTIONS.map((o) => {
                      const selected =
                        (form.user_status ?? "ACTIVE") === o.value;
                      return (
                        <Chip
                          key={o.value}
                          label={o.label}
                          size="small"
                          variant={selected ? "filled" : "outlined"}
                          color={STATUS_CHIP_COLOR[o.value] ?? "default"}
                          disabled={busy}
                          onClick={() => {
                            if (!busy) {
                              setForm((p) => ({ ...p, user_status: o.value }));
                            }
                          }}
                          sx={{
                            cursor: busy ? "default" : "pointer",
                            fontWeight: selected ? 600 : 500,
                            lineHeight: 1,
                          }}
                        />
                      );
                    })}
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Box>
            <Typography variant="overline" sx={sectionLabelSx}>
              Barcode
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "grey.50",
                px: 1.25,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={generateBarcode}
                    onChange={(e) => setGenerateBarcode(e.target.checked)}
                    disabled={busy}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Assign barcode</Typography>}
              />
              {generateBarcode && form.user_barcode ? (
                <>
                  {barcodePreviewUrl ? (
                    <Box
                      component="img"
                      src={barcodePreviewUrl}
                      alt=""
                      sx={{
                        height: 26,
                        width: "auto",
                        display: "block",
                        filter: "contrast(1.15)",
                      }}
                    />
                  ) : null}
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      color: "text.secondary",
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {form.user_barcode}
                  </Typography>
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Regenerate barcode"
                    onClick={handleRegenerateBarcode}
                    disabled={busy}
                    sx={{ p: 0.35 }}
                  >
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Download barcode"
                    onClick={downloadBarcodePng}
                    disabled={busy}
                    sx={{ p: 0.35 }}
                  >
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </>
              ) : null}
            </Box>
          </Box>

          {form.roles.includes(1) ? (
            <Box>
              <Typography variant="overline" sx={sectionLabelSx}>
                Stylist availability
              </Typography>
              <Box>
                <TableContainer
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflowY: "hidden",
                    bgcolor: "background.paper",
                  }}
                >
                  <Table size="small" aria-label="stylist availability table">
                    <TableHead sx={{ bgcolor: "secondary.main" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 140,
                            py: 0.75,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Day
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 120,
                            py: 0.75,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Available
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 170,
                            py: 0.75,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          Start
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: 170,
                            py: 0.75,
                            color: "common.white",
                            bgcolor: "secondary.main",
                          }}
                        >
                          End
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(DayOfWeek).map(([dayKey, dayValue]) => {
                        const dayData = form.availability?.[dayKey] || {};
                        const available = dayData.isAvailable !== false;
                        return (
                          <TableRow
                            key={dayKey}
                            sx={{
                              "&:last-child td, &:last-child th": {
                                borderBottom: 0,
                              },
                            }}
                          >
                            <TableCell
                              component="th"
                              scope="row"
                              sx={{ py: 0.5 }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                {dayValue}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <FormControlLabel
                                sx={{
                                  m: 0,
                                  "& .MuiFormControlLabel-label": { ml: 0.5 },
                                }}
                                control={
                                  <Checkbox
                                    color="primary"
                                    checked={available}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setForm((prev) => ({
                                        ...prev,
                                        availability: {
                                          ...prev.availability,
                                          [dayKey]: {
                                            startTime:
                                              prev.availability[dayKey]
                                                ?.startTime || "09:00",
                                            endTime:
                                              prev.availability[dayKey]
                                                ?.endTime || "17:00",
                                            isAvailable: checked,
                                          },
                                        },
                                      }));
                                    }}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">On</Typography>
                                }
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                type="time"
                                size={fieldSize}
                                fullWidth
                                disabled={!available || busy}
                                value={
                                  form.availability[dayKey]?.startTime ||
                                  "09:00"
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                                sx={{
                                  "& .MuiInputBase-root": { height: 36 },
                                  "& input": { py: 0.5 },
                                }}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((prev) => ({
                                    ...prev,
                                    availability: {
                                      ...prev.availability,
                                      [dayKey]: {
                                        ...prev.availability[dayKey],
                                        startTime: v,
                                      },
                                    },
                                  }));
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                type="time"
                                size={fieldSize}
                                fullWidth
                                disabled={!available || busy}
                                value={
                                  form.availability[dayKey]?.endTime || "17:00"
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 300 }}
                                sx={{
                                  "& .MuiInputBase-root": { height: 36 },
                                  "& input": { py: 0.5 },
                                }}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((prev) => ({
                                    ...prev,
                                    availability: {
                                      ...prev.availability,
                                      [dayKey]: {
                                        ...prev.availability[dayKey],
                                        endTime: v,
                                      },
                                    },
                                  }));
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ) : null}

          {showTimeOff ? (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="overline" sx={sectionLabelSx}>
                  Time off
                </Typography>
                {canManageTimeOff ? (
                  <MuiButton
                    type="button"
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => setTimeOffDialog({})}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Add time off
                  </MuiButton>
                ) : null}
              </Stack>
              <TimeOffList
                blocks={timeOffBlocks}
                canManage={canManageTimeOff}
                onEdit={(block) => setTimeOffDialog({ timeOff: block })}
                onDelete={handleDeleteTimeOff}
              />
              <TimeOffDialog
                open={Boolean(timeOffDialog)}
                timeOff={timeOffDialog?.timeOff}
                stylistId={Number(effectiveUserId)}
                stylists={[
                  {
                    user_id: Number(effectiveUserId),
                    user_name: `${form.user_first_name} ${form.user_last_name}`.trim(),
                  },
                ]}
                onClose={() => setTimeOffDialog(null)}
                onSaved={loadTimeOff}
              />
            </Box>
          ) : null}
        </Stack>
      </Box>

      <Box component="footer" sx={{ ...footerSx, position: "relative" }}>
        {isSubmitting ? (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 2,
            }}
          />
        ) : null}
        {effectiveUserId ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mr: "auto", flexWrap: "wrap" }}
            useFlexGap
          >
            <MuiButton
              type="button"
              color="error"
              variant="contained"
              size={fieldSize}
              disabled={busy}
              onClick={() => setIsConfirmationPopupVisible(true)}
            >
              Deactivate
            </MuiButton>
            <MuiButton
              type="button"
              variant="contained"
              color="secondary"
              size={fieldSize}
              disabled={busy}
              onClick={() => setIsResetPasswordConfirmOpen(true)}
            >
              Reset password
            </MuiButton>
          </Stack>
        ) : null}
        {onRequestClose ? (
          <MuiButton
            type="button"
            variant="outlined"
            color="secondary"
            size={fieldSize}
            disabled={busy}
            onClick={onRequestClose}
            sx={{
              textTransform: "none",
              minWidth: 100,
              color: (theme) =>
                theme.palette.mode === "dark" ? "#f2f2f2" : undefined,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.45)"
                  : undefined,
            }}
          >
            Cancel
          </MuiButton>
        ) : null}
        <MuiButton
          type="submit"
          variant="contained"
          color="primary"
          size={fieldSize}
          disabled={busy}
          sx={{
            minWidth: 120,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
              Saving…
            </>
          ) : effectiveUserId ? (
            "Save"
          ) : (
            "Create"
          )}
        </MuiButton>
      </Box>
    </form>
  );

  return (
    <>
      <Dialog
        open={isConfirmationPopupVisible}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Typography sx={{ px: 3, pt: 2, fontSize: "20px", fontWeight: 600 }}>
          Deactivate this account?
        </Typography>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            They will be set to inactive and will not be able to sign in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <MuiButton
            onClick={handleCancel}
            variant="outlined"
            color="secondary"
            size="small"
            sx={{
              textTransform: "none",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#f2f2f2" : undefined,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.45)"
                  : undefined,
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={() => void handleConfirm()}
            color="error"
            variant="contained"
            size="small"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Deactivate
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isResetPasswordConfirmOpen}
        onClose={handleResetPasswordCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Typography sx={{ px: 3, pt: 2, fontSize: "20px", fontWeight: 600 }}>
          Reset this user&apos;s password?
        </Typography>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            This will email a temporary password and set their status to Pending.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <MuiButton
            onClick={handleResetPasswordCancel}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={busy}
            sx={{
              textTransform: "none",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#f2f2f2" : undefined,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.45)"
                  : undefined,
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={() => void handleResetPasswordConfirm()}
            color="primary"
            variant="contained"
            size="small"
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Send reset
          </MuiButton>
        </DialogActions>
      </Dialog>

      {pageShell(<>{formBody}</>)}
    </>
  );
}
